import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { cloneDeep } from 'lodash';

import { useRegistries } from '@/react/portainer/registries/queries/useRegistries';

import { Button } from '@@/buttons';
import { createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import {
  EditableDatatable,
  NEW_ROW_ID,
} from '@@/datatables/editable/EditableDatatable';
import { isIdForNewRow } from '@@/datatables/editable/actionsColumn';

import { getColumns } from './getColumns';
import type { AccessRow, KubernetesRegistryFormValues } from './types';

const accessStorageKey = 'k8s-registry-access-table';
const accessSettingsStore = createPersistedStore(accessStorageKey);

interface Props {
  newIdCounter: number;
  setNewIdCounter: (newIdCounter: number) => void;
}

export function KubernetesRegistryAccessDatatable({
  newIdCounter,
  setNewIdCounter,
}: Props) {
  const {
    values: { accessRows },
    setFieldValue,
  } = useFormikContext<KubernetesRegistryFormValues>();
  const registriesQuery = useRegistries({
    select: (registries) => registries.filter((r) => r.Id !== 0),
  });
  const registries = registriesQuery.data || [];

  const tableState = useTableState(accessSettingsStore, accessStorageKey);
  const [newEditableIdCounter, setNewEditableIdCounter] = useState(NEW_ROW_ID);

  // Map registry IDs to names
  const registryMap = new Map(registries.map((r) => [r.Id, r.Name]));

  // Add registry names to rows for display
  const rowsWithNames: AccessRow[] = accessRows.map((row) => ({
    ...row,
    registryName:
      (row.registryId && registryMap.get(row.registryId)) || 'Unknown',
  }));

  const availableNamespaces = useMemo(
    () => Array.from(new Set(accessRows.flatMap((a) => a.namespaces || []))),
    [accessRows]
  );

  return (
    <EditableDatatable
      title="Registry access list"
      dataset={rowsWithNames}
      columns={getColumns(
        accessRows,
        onChange,
        (item) => handleRemoveRows([item]),
        handleNamespacesChange,
        availableNamespaces
      )}
      acceptRow={acceptRow}
      revertRow={revertRow}
      settingsManager={tableState}
      isRowSelectable={() => true}
      renderTableActions={(selectedRows) => (
        <>
          <Button
            icon={Plus}
            color="primary"
            size="small"
            onClick={addAccess}
            /* currently limit to 1 new row */
            disabled={accessRows.some((row) => isIdForNewRow(row.id))}
            data-cy="k8s-registry-access-datatable-add-access-button"
            className="!ml-0"
          >
            Add Access
          </Button>
          <Button
            color="dangerlight"
            size="small"
            icon={Trash2}
            onClick={() => handleRemoveRows(selectedRows)}
            data-cy="k8s-registry-access-datatable-remove-access-button"
            disabled={selectedRows.length === 0}
          >
            Remove
          </Button>
        </>
      )}
      data-cy="k8s-registry-access-datatable"
      emptyContentLabel="No registry access configured"
      extendTableOptions={(opts) => ({ ...opts, autoResetPageIndex: false })}
    />
  );

  function onChange(val: AccessRow[]) {
    return setFieldValue('accessRows', val);
  }

  function handleRemoveRows(items: AccessRow[]) {
    const idsToRemove = new Set(items.map((item) => item.registryId));
    const newAccessRows = accessRows.filter(
      (row) => !idsToRemove.has(row.registryId)
    );
    onChange(newAccessRows);
  }

  function handleNamespacesChange(item: AccessRow, namespaces: string[]) {
    const newAccessRows = [...accessRows];
    const index = newAccessRows.findIndex(
      (row) => row.registryId === item.registryId
    );
    if (index !== -1) {
      newAccessRows[index] = { ...newAccessRows[index], namespaces };
      onChange(newAccessRows);
    }
  }

  function revertRow(rowIndex: number) {
    onChange([
      ...accessRows.slice(0, rowIndex),
      ...accessRows.slice(rowIndex + 1),
    ]);
  }

  function acceptRow(rowIndex: number) {
    const existingRowIndex = accessRows.findIndex(
      (a, index) =>
        index !== rowIndex &&
        !isIdForNewRow(a.id) &&
        a.registryId === accessRows[rowIndex].registryId
    );
    const newRow = cloneDeep(accessRows[rowIndex]);

    if (existingRowIndex === -1) {
      newRow.id = newIdCounter;
      setNewIdCounter(newIdCounter + 1);
      const newAccessRows = [
        newRow,
        ...accessRows.slice(0, rowIndex),
        ...accessRows.slice(rowIndex + 1),
      ];
      onChange(newAccessRows);
    } else {
      const newAccessRows = [
        ...accessRows.slice(0, rowIndex),
        ...accessRows.slice(rowIndex + 1),
      ];
      newAccessRows[existingRowIndex - 1].registryId = newRow.registryId;
      newAccessRows[existingRowIndex - 1].registryName = newRow.registryName;
      newAccessRows[existingRowIndex - 1].namespaces = newRow.namespaces;
      onChange(newAccessRows);
    }
  }

  function addAccess() {
    const newAccessRow: AccessRow = {
      id: newEditableIdCounter,
      registryId: null,
      registryName: '',
      namespaces: [],
    };

    const newAccessRows = [cloneDeep(newAccessRow), ...accessRows];
    onChange(newAccessRows);
    setNewEditableIdCounter((count) => count - 1);
  }
}
